/**
 * `/draws/:drawId` — the §16.2 draw control room as a control surface (D-2607): §9.5 gate checklist →
 * arm (two-person controls) → reveal → fallback / redraw, with fairness facts and the audit in a rail. Every
 * command is a server-side protocol step; no control here sets a winner (§9.4).
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { DrawAdminDto, RedrawReasonCode, RevealNextResponse } from '@windrop/contracts';
import { Button, Field, Input, Select, Skeleton, TierReveal, useIntl } from '@windrop/ui';
import { CheckCircle2, CircleDashed, XCircle } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams } from 'react-router';
import { Can } from '../../auth/guards';
import { useSession } from '../../auth/session';
import { StepUpCancelled, useStepUp } from '../../auth/step-up';
import { Callout } from '../../components/Callout';
import { Card } from '../../components/Card';
import { ConfirmDestructive } from '../../components/ConfirmDestructive';
import { EmptyState, ErrorState } from '../../components/EmptyState';
import { PageHeader } from '../../components/PageHeader';
import { DetailSkeleton } from '../../components/PageSkeleton';
import { StatCard } from '../../components/StatCard';
import { StatusBadge, ToneBadge, humanizeStatus } from '../../components/StatusBadge';
import { Timeline } from '../../components/Timeline';
import { isApiError } from '../../lib/api/client';
import { drawsApi } from './api';

const errorText = (e: unknown): string | null =>
  e instanceof StepUpCancelled
    ? ''
    : isApiError(e)
      ? (e.problem?.detail ?? e.message)
      : e instanceof Error
        ? e.message
        : String(e);

const STATUS_RANK: Record<DrawAdminDto['status'], number> = {
  COMMITTED: 0,
  SNAPSHOT_LOCKED: 0,
  ALLOCATED: 1,
  PUBLISHED: 2,
  REVEALED: 3,
  VERIFIED: 4,
  FAILED: -1,
  CANCELLED: -1,
};

function ControlStep({
  n,
  title,
  state,
  children,
}: {
  n: number;
  title: string;
  state: 'done' | 'current' | 'locked';
  children: React.ReactNode;
}) {
  return (
    <div className="wdc-control-step" data-state={state}>
      <span className="wdc-control-step__n" aria-hidden="true">
        {state === 'done' ? '✓' : n}
      </span>
      <div className="wd-stack" style={{ gap: 'var(--wd-space-3)' }}>
        <h3>
          {title}
          {state === 'done' ? <span className="wd-sr-only"> (done)</span> : null}
        </h3>
        {children}
      </div>
    </div>
  );
}

export function DrawRoomPage() {
  const { drawId = '' } = useParams();
  const qc = useQueryClient();
  const intl = useIntl();
  const { session } = useSession();
  const { withStepUp } = useStepUp();
  const draw = useQuery({
    queryKey: ['draws', 'detail', drawId],
    queryFn: () => drawsApi.get(drawId),
    enabled: Boolean(drawId),
    refetchInterval: 15_000,
  });
  const precheck = useQuery({
    queryKey: ['draws', 'precheck', drawId],
    queryFn: () => drawsApi.precheck(drawId),
    enabled: Boolean(drawId),
  });
  const audit = useQuery({
    queryKey: ['draws', 'audit', drawId],
    queryFn: () => drawsApi.audit(drawId),
    enabled: Boolean(drawId),
  });
  const rehearsal = useQuery({
    queryKey: ['draws', 'rehearsal', drawId],
    queryFn: () => drawsApi.rehearsal(drawId),
    enabled: Boolean(drawId),
  });
  const [secondOperatorId, setSecondOperatorId] = useState('');
  const [message, setMessage] = useState<{ tone: 'success' | 'error' | 'info'; text: string } | null>(null);
  const [pacing, setPacing] = useState(false);
  const [lastTier, setLastTier] = useState<RevealNextResponse['tier']>(null);
  const [publishOpen, setPublishOpen] = useState(false);
  const [redrawOpen, setRedrawOpen] = useState(false);
  const [redraw, setRedraw] = useState<{ winId: string; reasonCode: RedrawReasonCode }>({
    winId: '',
    reasonCode: 'unclaimed',
  });
  const invalidate = () =>
    Promise.all([
      qc.invalidateQueries({ queryKey: ['draws'] }),
      qc.invalidateQueries({ queryKey: ['lottery'] }),
    ]);

  const run = useMutation({
    mutationFn: (d: DrawAdminDto) =>
      withStepUp(
        () => drawsApi.run(d.lotteryId, { secondOperatorId: secondOperatorId || undefined, execute: 'now' }),
        { permission: 'draw.run', reason: `Run the draw of ${d.title}.` },
      ),
    onSuccess: async (r) => {
      setMessage({
        tone: 'success',
        text: r.replayed
          ? `Draw ${r.drawId} already ran — committed result returned (${r.status}).`
          : `Draw allocated: ${r.winners} winners, ${r.sponsorSamples} sponsor samples (${r.status}).`,
      });
      await invalidate();
    },
    onError: (e) => setMessage(errorText(e) ? { tone: 'error', text: errorText(e) ?? '' } : null),
  });
  const revealNext = useMutation({
    mutationFn: (all: boolean) =>
      withStepUp(
        () => drawsApi.revealNext(drawId, { all, secondOperatorId: secondOperatorId || undefined }),
        { permission: 'draw.reveal', reason: 'Reveal the next tier to the live room.' },
      ),
    onSuccess: async (r) => {
      setLastTier(r.tier);
      setMessage({
        tone: 'info',
        text: r.complete
          ? `Every tier is revealed — results published (${r.status}).`
          : `Revealed tier ${r.revealedTiers} of ${r.totalTiers}.`,
      });
      if (r.complete) setPacing(false);
      await invalidate();
    },
    onError: (e) => {
      setPacing(false);
      setMessage(errorText(e) ? { tone: 'error', text: errorText(e) ?? '' } : null);
    },
  });
  const publishStatic = useMutation({
    mutationFn: () =>
      withStepUp(() => drawsApi.publishStatic(drawId), {
        permission: 'draw.reveal',
        reason: 'Publish the static results and reveal the seed.',
      }),
    onSuccess: async (r) => {
      setPublishOpen(false);
      setMessage({
        tone: 'success',
        text: `Static results published and seed revealed (${r.status}). Bundle: ${r.bundleUrl}`,
      });
      await invalidate();
    },
    onError: (e) => setMessage(errorText(e) ? { tone: 'error', text: errorText(e) ?? '' } : null),
  });
  const verify = useMutation({
    mutationFn: () => drawsApi.verify(drawId),
    onSuccess: async (r) => {
      setMessage({
        tone: r.ok ? 'success' : 'error',
        text: r.ok
          ? `Verifier PASS (${r.verifierVersion}) — status ${r.status}.`
          : `Verifier FAIL: ${r.mismatches.join('; ')}`,
      });
      await invalidate();
    },
  });
  const doRedraw = useMutation({
    mutationFn: ({ reason }: { reason: string }) =>
      withStepUp(
        () =>
          drawsApi.redraw(drawId, {
            winId: redraw.winId,
            reason,
            reasonCode: redraw.reasonCode,
            secondOperatorId: secondOperatorId || undefined,
          }),
        { permission: 'draw.redraw', reason: `Redraw win ${redraw.winId}.` },
      ),
    onSuccess: async (r) => {
      setRedrawOpen(false);
      setMessage({
        tone: 'success',
        text: `Redraw ${r.redrawId}: new draw ${r.newDrawId}, replacement ${r.replacementWinId ?? 'none (no eligible entries)'} — ${r.status}.`,
      });
      await invalidate();
    },
    onError: (e) => setMessage(errorText(e) ? { tone: 'error', text: errorText(e) ?? '' } : null),
  });

  // Suspense pacing: when enabled, reveal the next tier after the rehearsal's suggested pause (operator can stop).
  const d = draw.data;
  const pacingRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    if (!pacing || !d || d.revealedTiers >= d.totalTiers || revealNext.isPending) return;
    const step = rehearsal.data?.steps[d.revealedTiers];
    pacingRef.current = setTimeout(() => revealNext.mutate(false), step?.suggestedPauseMs ?? 6000);
    return () => {
      if (pacingRef.current) clearTimeout(pacingRef.current);
    };
  }, [pacing, d, rehearsal.data, revealNext]);

  const kitTiers = useMemo(
    () =>
      d
        ? d.tiers.map((t, index) => ({
            id: t.id,
            name: `${humanizeStatus(t.tier)} — ${t.prizeName}`,
            prizeText: t.prizeName,
            total: t.total,
            winners:
              (t.revealed || index < d.revealedTiers) && lastTier?.prizeId === t.prizeId
                ? lastTier.winners.map((w) => ({
                    winId: w.winId ?? '',
                    displayName: w.display,
                    entryRef: w.entryId,
                  }))
                : t.revealed || index < d.revealedTiers
                  ? Array.from({ length: t.allocated }, (_, i) => ({
                      winId: `revealed-${i + 1}`,
                      displayName: `Winner ${i + 1} (see results page)`,
                    }))
                  : [],
          }))
        : [],
    [d, lastTier],
  );

  if (draw.isPending) return <DetailSkeleton label="Loading draw" />;
  if (draw.isError || !d) {
    return (
      <ErrorState
        title="Draw not found"
        body="It may belong to another tenant or the id is wrong."
        reference={isApiError(draw.error) ? draw.error.supportReference : undefined}
        action={<Button href="/draws">Back to the draw control room</Button>}
      />
    );
  }
  const canRun = d.status === 'COMMITTED' || d.status === 'SNAPSHOT_LOCKED';
  const canReveal = d.status === 'ALLOCATED' || (d.status === 'PUBLISHED' && d.revealedTiers < d.totalTiers);
  const canPublishStatic = d.status === 'ALLOCATED' || d.status === 'PUBLISHED';
  const needsSecond = d.highValue && !d.secondOperatorId;
  const secondMissing = needsSecond && !secondOperatorId;
  const rank = STATUS_RANK[d.status];
  const gatesReady = precheck.data?.ready ?? false;
  const stepState = (index: number): 'done' | 'current' | 'locked' => {
    // 0 gates · 1 arm/run · 2 reveal
    if (index === 0) return gatesReady || rank >= 1 ? 'done' : 'current';
    if (index === 1) return rank >= 1 ? 'done' : gatesReady ? 'current' : 'locked';
    return rank >= 3 ? 'done' : rank >= 1 ? 'current' : 'locked';
  };

  return (
    <>
      <PageHeader
        breadcrumb={[{ label: 'Draw control room', to: '/draws' }, { label: d.title }]}
        kicker={`${humanizeStatus(d.mechanic)} · draw ${d.drawId}`}
        title={d.title}
        meta={
          <>
            <StatusBadge kind="DrawStatus" value={d.status} />
            {d.highValue ? <ToneBadge tone="warning">Two operators</ToneBadge> : null}
          </>
        }
        description={`${d.slug} · config v${d.configVersion} · lottery ${humanizeStatus(d.lotteryStatus)}${d.drawAt ? ` · draw ${intl.formatDateTime(d.drawAt)}` : ''}`}
        actions={
          <>
            <Button variant="secondary" href={`/lotteries/${d.lotteryId}/draw`}>
              Lottery draw tab
            </Button>
            <Button variant="secondary" href={d.resultsUrl} target="_blank" rel="noreferrer">
              Public results
            </Button>
          </>
        }
      />
      {message ? (
        <p role="status" className="wdc-callout" data-tone={message.tone} data-testid="room-message">
          {message.text}
        </p>
      ) : null}

      <div className="wdc-stats">
        <StatCard label="Eligible entries" value={d.counts.eligible} hint={`${d.counts.excluded} excluded`} />
        <StatCard
          label="Winners"
          value={d.counts.winners}
          hint={`${d.counts.sponsorSamples} sponsor samples`}
        />
        <StatCard label="Tiers revealed" value={`${d.revealedTiers} / ${d.totalTiers}`} />
        {d.poolSize ? (
          <StatCard
            label="Pool tickets played"
            value={`${d.counts.ticketsAssigned ?? 0} / ${d.poolSize}`}
            hint={`pool hash ${d.poolVectorHash?.slice(0, 12)}…`}
          />
        ) : (
          <StatCard label="Draw time" value={d.drawAt ? intl.formatDateTime(d.drawAt) : 'At close'} />
        )}
      </div>

      <div className="wdc-layout">
        <div className="wd-stack">
          <Card
            title="Control surface"
            description="Gates → arm → reveal. Each control is a server protocol step; nothing here chooses a winner (§9.4)."
            titleId="controls"
          >
            <ControlStep n={1} title="§9.5 pre-draw gates" state={stepState(0)}>
              {precheck.isPending ? (
                <Skeleton shape="block" height={120} label="Running checks" />
              ) : precheck.isError ? (
                <p className="wd-muted">Pre-draw checks are unavailable right now.</p>
              ) : (
                <>
                  <p className="wd-row">
                    {precheck.data.ready ? (
                      <ToneBadge tone="success">Ready to draw</ToneBadge>
                    ) : (
                      <ToneBadge tone="warning">Not ready</ToneBadge>
                    )}
                    <span className="wd-muted wdc-small">
                      checked {intl.formatRelative(precheck.data.checkedAt)}
                    </span>
                  </p>
                  <table className="wdc-table" data-testid="gates-table">
                    <caption className="wd-sr-only">Pre-draw gates</caption>
                    <thead>
                      <tr>
                        <th scope="col">Gate</th>
                        <th scope="col">Result</th>
                        <th scope="col">Detail</th>
                      </tr>
                    </thead>
                    <tbody>
                      {precheck.data.gates.map((g) => (
                        <tr key={g.gate}>
                          <th scope="row">{humanizeStatus(g.gate)}</th>
                          <td>
                            {g.ok ? (
                              <ToneBadge tone="success">
                                <CheckCircle2 size={12} aria-hidden="true" /> Pass
                              </ToneBadge>
                            ) : (
                              <ToneBadge tone="error">
                                <XCircle size={12} aria-hidden="true" /> Blocked
                              </ToneBadge>
                            )}
                          </td>
                          <td>{g.detail}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </>
              )}
            </ControlStep>

            <hr />

            <ControlStep n={2} title="Arm and run" state={stepState(1)}>
              <p className="wd-muted wdc-small">
                {d.highValue
                  ? `High-value draw (prize value at or above ${d.highValueThresholdMinor} minor): a second authorized operator must confirm every run, reveal and redraw (§9.5).`
                  : 'Single-operator draw.'}
                {d.secondOperatorId ? ` Second operator recorded: ${d.secondOperatorId}.` : ''}
              </p>
              {needsSecond ? (
                <div className="wdc-narrow">
                  <Field
                    label="Second operator (staff id)"
                    hint="A different, active draw operator or lottery approver. Required for high-value draws."
                  >
                    <Input
                      value={secondOperatorId}
                      onChange={(e) => setSecondOperatorId(e.target.value.trim())}
                      data-testid="second-operator"
                    />
                  </Field>
                </div>
              ) : null}
              <div className="wd-btn-group">
                <Can permission="draw.run">
                  <Button
                    onClick={() => run.mutate(d)}
                    disabled={!canRun || secondMissing}
                    disabledReason={!canRun ? `Draw is ${d.status}` : 'Second operator required'}
                    loading={run.isPending}
                    data-testid="run-draw"
                  >
                    Run draw
                  </Button>
                </Can>
                <Can permission="draw.audit.read">
                  <Button
                    variant="secondary"
                    onClick={() => verify.mutate()}
                    loading={verify.isPending}
                    disabled={d.status !== 'REVEALED' && d.status !== 'VERIFIED'}
                    disabledReason="Available once the seed is revealed"
                    data-testid="verify"
                  >
                    Re-run verifier
                  </Button>
                </Can>
                {d.bundleAvailable ? (
                  <Button
                    variant="secondary"
                    href={drawsApi.bundleUrl(d.drawId)}
                    download
                    data-testid="export-bundle"
                  >
                    Export fairness bundle
                  </Button>
                ) : null}
                <Button variant="quiet" href={d.fairnessUrl} target="_blank" rel="noreferrer">
                  Public fairness record
                </Button>
              </div>
              <p className="wd-muted wdc-small">
                Actor: {session?.name ?? '—'} ({session?.roles.join(', ')})
              </p>
            </ControlStep>

            <hr />

            <ControlStep n={3} title="Live tier reveal" state={stepState(2)}>
              {rehearsal.data ? (
                <details>
                  <summary>
                    Reveal rehearsal ({rehearsal.data.steps.length} steps, {rehearsal.data.totalWinners}{' '}
                    winners — counts only)
                  </summary>
                  <ol data-testid="rehearsal">
                    {rehearsal.data.steps.map((s) => (
                      <li key={s.index}>
                        {humanizeStatus(s.tier)} — {s.prizeName}: {s.winners} winner(s), suggested pause{' '}
                        {Math.round(s.suggestedPauseMs / 1000)} s
                      </li>
                    ))}
                  </ol>
                  <p className="wd-muted">{rehearsal.data.fallback.note}</p>
                </details>
              ) : null}
              <TierReveal
                tiers={kitTiers}
                currentTierId={kitTiers.find((t) => t.winners.length < t.total)?.id ?? null}
                fairnessHref={d.fairnessUrl}
                operator={{
                  canReveal: canReveal && !secondMissing && !revealNext.isPending,
                  onRevealNext: () => revealNext.mutate(false),
                  label: 'Reveal next tier',
                }}
              />
              <div className="wd-btn-group">
                <Can permission="draw.reveal">
                  <Button
                    variant="secondary"
                    onClick={() => setPacing((p) => !p)}
                    disabled={!canReveal || secondMissing}
                    disabledReason="Nothing to reveal"
                    data-testid="pacing-toggle"
                  >
                    {pacing ? 'Stop suspense pacing' : 'Start suspense pacing'}
                  </Button>
                  <Button
                    variant="secondary"
                    onClick={() => setPublishOpen(true)}
                    disabled={!canPublishStatic && d.status !== 'ALLOCATED'}
                    disabledReason={`Draw is ${d.status}`}
                    data-testid="publish-static"
                  >
                    Publish static fallback + reveal seed
                  </Button>
                </Can>
              </div>
            </ControlStep>
          </Card>

          <Card
            title="Audited redraw (§9.4)"
            description="Needs a reason, an approved eligibility snapshot, a new seed commitment and a new draw id. The replaced win is marked REDRAWN; the published list of this draw is never edited."
            tone="warning"
            titleId="redraw"
          >
            <div className="wdc-form-grid">
              <Field label="Win ID to redraw">
                <Input
                  value={redraw.winId}
                  onChange={(e) => setRedraw((r) => ({ ...r, winId: e.target.value }))}
                  placeholder="WIN-XXXXX-XXXXX"
                  data-testid="redraw-win"
                />
              </Field>
              <Field label="Reason code">
                <Select
                  value={redraw.reasonCode}
                  onChange={(e) =>
                    setRedraw((r) => ({ ...r, reasonCode: e.target.value as RedrawReasonCode }))
                  }
                  options={[
                    { value: 'unclaimed', label: 'Unclaimed after the claim window' },
                    { value: 'rejected_after_appeal', label: 'Claim rejected after appeal' },
                    { value: 'disqualified', label: 'Winner disqualified' },
                    { value: 'other', label: 'Other (reason required)' },
                  ]}
                />
              </Field>
            </div>
            <Can permission="draw.redraw">
              <div>
                <Button
                  variant="danger"
                  onClick={() => setRedrawOpen(true)}
                  disabled={!redraw.winId || secondMissing}
                  disabledReason="Enter the win ID (and the second operator for high-value draws)"
                  data-testid="redraw-open"
                >
                  Redraw this prize
                </Button>
              </div>
            </Can>
          </Card>
        </div>

        <div className="wdc-layout__rail">
          <Card title="Fairness record" as="h2">
            <dl className="wdc-facts" data-layout="stacked">
              <div>
                <dt>Seed commitment</dt>
                <dd>
                  <code className="wdc-hash">{d.seedCommit}</code>
                  <div className="wd-muted wdc-small">{intl.formatDateTime(d.committedAt)}</div>
                </dd>
              </div>
              <div>
                <dt>Snapshot hash</dt>
                <dd>
                  {d.snapshotHash ? (
                    <code className="wdc-hash">{d.snapshotHash}</code>
                  ) : (
                    <span className="wd-muted">Taken at draw</span>
                  )}
                </dd>
              </div>
              <div>
                <dt>Result hash</dt>
                <dd>
                  {d.resultHash ? (
                    <code className="wdc-hash">{d.resultHash}</code>
                  ) : (
                    <span className="wd-muted">—</span>
                  )}
                </dd>
              </div>
              <div>
                <dt>Algorithm</dt>
                <dd>
                  <code>{d.algorithm}</code>
                </dd>
              </div>
            </dl>
          </Card>
          <Card title="Draw audit" as="h2" titleId="audit">
            {audit.data ? (
              <>
                <p className="wd-muted wdc-small">
                  Operators:{' '}
                  {audit.data.operators.map((o) => `${o.role} ${o.staffId}`).join(' · ') || 'none yet'}
                  {audit.data.verification
                    ? ` · verifier ${audit.data.verification.ok ? 'PASS' : 'FAIL'} at ${intl.formatDateTime(audit.data.verification.checkedAt)}`
                    : ''}
                </p>
                <Timeline
                  events={audit.data.timeline.map((t) => ({
                    id: t.auditId,
                    at: t.at,
                    title: humanizeStatus(t.status),
                    actor: t.actorId,
                    tone:
                      t.status === 'FAILED' || t.status === 'CANCELLED'
                        ? 'error'
                        : t.status === 'VERIFIED'
                          ? 'success'
                          : 'info',
                  }))}
                  emptyText="No draw events yet."
                />
                <p className="wd-muted wdc-small">
                  <Link to={`/lotteries/${d.lotteryId}/audit`}>Full lottery audit trail</Link>
                </p>
              </>
            ) : audit.isError ? (
              <EmptyState
                title="Audit not available"
                body="The audit endpoint did not answer."
                action={<Button onClick={() => void audit.refetch()}>Retry</Button>}
              />
            ) : (
              <Skeleton shape="block" height={80} label="Loading audit" />
            )}
          </Card>
          {d.status === 'FAILED' || d.status === 'CANCELLED' ? (
            <Callout tone="error" title={`Draw ${humanizeStatus(d.status).toLowerCase()}`}>
              {d.failureReason ? <p>{d.failureReason}</p> : null}
            </Callout>
          ) : null}
          <p className="wd-muted wdc-small wd-row">
            <CircleDashed size={14} aria-hidden="true" /> Status refreshes every 15 s.
          </p>
        </div>
      </div>

      <ConfirmDestructive
        open={publishOpen}
        onOpenChange={setPublishOpen}
        title="Publish the static results and reveal the seed"
        objectName={d.slug}
        objectKind="lottery"
        consequence="Every remaining tier is revealed at once, the lottery moves to DRAWN, the seed and the fairness bundle become public and the verifier runs. This cannot be undone."
        busy={publishStatic.isPending}
        error={publishStatic.isError ? errorText(publishStatic.error) || null : null}
        onConfirm={() => publishStatic.mutate()}
      />
      <ConfirmDestructive
        open={redrawOpen}
        onOpenChange={setRedrawOpen}
        title={`Redraw ${redraw.winId}`}
        objectName={d.slug}
        objectKind="lottery"
        consequence="The current win becomes REDRAWN, its prize unit is re-issued, a new seed is committed and a replacement is drawn from the approved eligibility snapshot (one redraw per prize by default, §2.4)."
        requireReason
        busy={doRedraw.isPending}
        error={doRedraw.isError ? errorText(doRedraw.error) || null : null}
        onConfirm={({ reason }) => doRedraw.mutate({ reason: reason ?? '' })}
      />
    </>
  );
}
